/**
 * Contract cash waterfall panel (The Desk) — v2 handoff.
 *
 * Stacked bars per season: guaranteed (brass), at-risk (slate), option
 * (outline); agency fee as a line. Literal hexes for the same reason as
 * chartTheme: these are SVG attributes, matching styles/themes.css tokens.
 *
 * Coverage is stated, never implied: the note names how many contracts
 * feed the chart and how many carry no season rows. With no rows at all,
 * the empty treatment says exactly what will populate the panel.
 */

import { useMemo } from 'react'
import { computeCashWaterfall, type CashWaterfall as Model } from '../../utils/cashWaterfall'
import { money, axis } from '../../utils/format'
import type { Contract } from '../../types'
import styles from './TheDesk.module.css'

const C = {
  guaranteed: '#C8A45C',
  atRisk: '#4A5561',
  optionStroke: '#3A4149',
  fee: '#4E9A6A',
  grid: '#181C20',
  axisText: '#6E757D',
  totalText: '#A2A8B0',
  yearText: '#8B9299',
  canvas: '#0A0B0C',
}

function niceCeiling(v: number): number {
  if (v <= 0) return 1
  const mag = 10 ** Math.floor(Math.log10(v))
  for (const m of [1, 2, 2.5, 5, 10]) {
    if (v <= m * mag) return m * mag
  }
  return 10 * mag
}

function WaterfallSvg({ model }: { model: Model }) {
  const { seasons } = model
  const W = 900
  const H = 208
  const padL = 44
  const padR = 34
  const padB = 26
  const padT = 14
  const iw = W - padL - padR
  const ih = H - padB - padT
  const max = niceCeiling(Math.max(...seasons.map(s => s.guaranteed + s.atRisk + s.option)))
  const bw = iw / seasons.length
  const cw = Math.min(bw * 0.5, 90)
  const yScale = (v: number) => padT + ih - (v / max) * ih

  const parts: JSX.Element[] = []

  // Gridlines at quarter steps of the scale, axis labels in the house axis format.
  for (let step = 0; step <= 4; step++) {
    const v = (max / 4) * step
    const y = yScale(v)
    parts.push(<line key={`g${step}`} x1={padL} y1={y} x2={W - padR} y2={y} stroke={C.grid} strokeWidth={1} />)
    parts.push(
      <text key={`gl${step}`} x={padL - 8} y={y + 3.5} textAnchor="end" fontFamily="IBM Plex Mono, monospace" fontSize={11} fill={C.axisText}>
        {axis(v)}
      </text>,
    )
  }

  const feePts: { x: number; y: number }[] = []
  seasons.forEach((s, i) => {
    const x = padL + i * bw + (bw - cw) / 2
    let acc = 0
    const seg = (val: number, key: string, fill: string, stroke?: string) => {
      const h = (val / max) * ih
      if (h <= 0) return
      const y = padT + ih - h - acc
      parts.push(
        <rect key={`${s.year}-${key}`} x={x} y={y} width={cw} height={h} fill={fill} stroke={stroke} strokeWidth={stroke ? 1 : undefined} />,
      )
      acc += h
    }
    seg(s.guaranteed, 'g', C.guaranteed)
    seg(s.atRisk, 'r', C.atRisk)
    seg(s.option, 'o', 'none', C.optionStroke)

    const total = s.guaranteed + s.atRisk + s.option
    parts.push(
      <text key={`${s.year}-t`} x={x + cw / 2} y={padT + ih - acc - 6} textAnchor="middle" fontFamily="IBM Plex Mono, monospace" fontSize={12} fill={C.totalText}>
        {money(total)}
      </text>,
    )
    parts.push(
      <text key={`${s.year}-y`} x={x + cw / 2} y={H - 8} textAnchor="middle" fontFamily="IBM Plex Mono, monospace" fontSize={12} fill={C.yearText}>
        {s.year}
      </text>,
    )
    if (s.fee > 0) feePts.push({ x: x + cw / 2, y: yScale(s.fee) })
  })

  if (feePts.length > 0) {
    parts.push(
      <polyline
        key="fee"
        points={feePts.map(p => `${p.x},${p.y}`).join(' ')}
        fill="none"
        stroke={C.fee}
        strokeWidth={1.25}
      />,
    )
    feePts.forEach((p, i) => {
      parts.push(<circle key={`fp${i}`} cx={p.x} cy={p.y} r={2} fill={C.canvas} stroke={C.fee} strokeWidth={1.25} />)
    })
    const last = feePts[feePts.length - 1]
    parts.push(
      <text key="feeLabel" x={W - padR + 6} y={last.y + 3.5} fontFamily="IBM Plex Mono, monospace" fontSize={11} fill={C.fee}>
        fee
      </text>,
    )
  }

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block' }} preserveAspectRatio="none" role="img" aria-label="Contract cash by season">
      {parts}
    </svg>
  )
}

function LegendSwatch({ color, outline, label }: { color?: string; outline?: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <span style={{ width: 8, height: 8, background: color ?? 'none', border: outline ? `1px solid ${outline}` : undefined, display: 'inline-block' }} />
      <span style={{ fontSize: 12, color: '#A2A8B0', letterSpacing: '.05em' }}>{label}</span>
    </span>
  )
}

export default function CashWaterfall({ contracts, loading }: { contracts: Contract[]; loading: boolean }) {
  const model = useMemo(() => computeCashWaterfall(contracts), [contracts])
  const hasData = model.seasons.length > 0

  return (
    <div className={styles.panel}>
      <div className={styles.panelHead}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, minWidth: 0 }}>
          <h2 className={styles.panelTitle}>Contract cash waterfall</h2>
          <span className={styles.panelMeta}>Committed vs. at-risk, by season</span>
        </div>
        {hasData && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <LegendSwatch color={C.guaranteed} label="Guaranteed" />
            <LegendSwatch color={C.atRisk} label="At-risk" />
            <LegendSwatch outline={C.optionStroke} label="Option" />
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 11, height: 1.5, background: C.fee, display: 'inline-block' }} />
              <span style={{ fontSize: 12, color: '#A2A8B0', letterSpacing: '.05em' }}>Agency fee</span>
            </span>
          </div>
        )}
      </div>

      {!hasData ? (
        <div className={styles.empty}>
          {loading
            ? 'Loading…'
            : 'No season breakdowns recorded. Add per-year figures (base, bonus, guaranteed) on a contract and each season’s committed and at-risk cash appears here.'}
        </div>
      ) : (
        <>
          <WaterfallSvg model={model} />
          <div className={styles.note}>
            {model.covered} of {model.covered + model.uncovered} contracts carry season rows and are shown;{' '}
            {model.uncovered > 0 ? `${model.uncovered} without rows ${model.uncovered === 1 ? 'is' : 'are'} not represented. ` : ''}
            Fee line covers the {model.feeCovered} with a recorded commission rate. Option seasons are the final{' '}
            recorded option years of each deal.
          </div>
        </>
      )}
    </div>
  )
}
